import { Box, CloseButton, Dialog, IconButton, Stack, Typography } from "@compass/core";
import { CustomFilter, ListFilterBase } from "generated/openApi";
import { useEffect, useState } from "react";
import { Breakpoint } from "@mui/material";
import PlaylistAddCheckCircleIcon from "@mui/icons-material/PlaylistAddCheckCircle";
import PlaylistAddCircleIcon from "@mui/icons-material/PlaylistAddCircle";
import ClearIcon from "@mui/icons-material/Clear";
import { SubmitButton } from "components";

export const useListFilter = <T extends ListFilterBase>(props: {
  listFilter: T;
  initListFilter?: T;
  onApply: (listFilter: T) => void;
  countFilter?: T;
  inputNode: (listFilter: T, setListFilter: (value: T) => void) => React.ReactNode;
  maxWidth?: false | Breakpoint | undefined;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [listFilter, setListFilter] = useState<T>({} as T);

  useEffect(() => {
    if (open) {
      setListFilter(props.listFilter);
    }
  }, [open]);

  const normalizeFilter = (filter: T) => ({
    ...filter,
    page: 0,
    pageSize: 0,
    sortObjects: undefined,
    searchString: undefined,
  });

  const isFilterModified = (filterToCheck: T) => {
    if (!props.initListFilter) return false;
    return JSON.stringify(normalizeFilter(filterToCheck)) !== JSON.stringify(normalizeFilter(props.initListFilter));
  };

  const canApply = () => {
    return JSON.stringify(normalizeFilter(listFilter)) !== JSON.stringify(normalizeFilter(props.listFilter));
  };

  const canReset = () => isFilterModified(listFilter);

  const canClearAppliedFilters = () => {
    return hasFilterValues({
      ...props.listFilter,
      page: null,
      pageSize: null,
      sortObjects: null,
      searchString: null,
    });
  };

  const apply = () => {
    props.onApply(listFilter);
    setOpen(false);
  };

  const reset = () => {
    props.onApply(props.initListFilter!);
    setListFilter(props.initListFilter!);
  };

  const clearAppliedFilters = () => {
    const clearedFilter = {
      page: props.listFilter.page,
      pageSize: props.listFilter.pageSize,
      sortObjects: props.listFilter.sortObjects,
      searchString: props.listFilter.searchString,
    } as T;
    props.onApply(clearedFilter);
  };

  function hasFilterValues(object: any, level: number = 0): boolean {
    for (const property in object) {
      if (object.hasOwnProperty(property)) {
        const value = object[property];
        //console.log(`Checking property: ${property}, value: ${value}`);

        if (value === null || value === undefined || value === "") continue;

        if (property === "CustomFilters") {
          const customFilters: CustomFilter[] = value;
          const hasValidCustomFilter = customFilters.some((x) => x.isValid === true);

          if (hasValidCustomFilter) {
            return true; // Return true as soon as a valid custom filter is found
          }
        } else if (value === true || value === false) {
          return true; // Return true as soon as a boolean value is found
        } else if (Array.isArray(value) && value.length > 0) {
          return true; // Return true as soon as a non-empty array is found
        } else if (typeof value === "object") {
          // Recurse for nested objects
          const hasValidNestedValue = hasFilterValues(object[property], level + 1);
          if (hasValidNestedValue) {
            return true; // Return true as soon as a nested object with valid values is found
          }
        } else if (value !== null || value !== undefined || value !== "") {
          return true; // Return true for any other non-null/undefined/empty value
        }
      }
    }

    return false; // Return false if no valid values are found after checking all properties
  }

  const countFilter = props.countFilter ? props.countFilter : props.listFilter;
  const _hasFilterValues = hasFilterValues({
    ...countFilter,
    page: null,
    pageSize: null,
    sortObjects: null,
    searchString: null,
  });

  const outputNode = (
    <>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <IconButton
          onClick={() => setOpen(true)}
          color={_hasFilterValues ? "success" : undefined}
          size="small"
        >
          {_hasFilterValues && <PlaylistAddCheckCircleIcon sx={{ fontSize: "28px" }} />}
          {!_hasFilterValues && <PlaylistAddCircleIcon sx={{ fontSize: "28px" }} />}
        </IconButton>

        {canClearAppliedFilters() && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              clearAppliedFilters();
            }}
            size="small"
            title="Clear filters"
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              padding: "2px",
              backgroundColor: "grey.300",
              "&:hover": {
                backgroundColor: "grey.400",
              },
              minWidth: "18px",
              width: "18px",
              height: "18px",
            }}
          >
            <ClearIcon sx={{ fontSize: "12px" }} />
          </IconButton>
        )}
      </Box>

      {open && (
        <Dialog
          isForm
          open
          onClose={() => setOpen(false)}
          maxWidth={props.maxWidth}
          header={
            <Stack direction="row" justifyContent={"space-between"} alignItems="center">
              <Typography variant="inherit">Filters</Typography>

              <Stack spacing="sm" direction="row" justifyContent="flex-end">

                <CloseButton theme="Dark" onClick={() => setOpen(false)}>Close</CloseButton>
                {props.initListFilter && (
                  <SubmitButton theme="Dark" label="Reset" variant="outlined" onClick={() => reset()} disabled={!canReset()} />
                )}

                <SubmitButton theme="Dark" label="Apply" variant="outlined" onClick={() => apply()} disabled={!canApply()} type="submit" />
              </Stack>
            </Stack>
          }
          content={props.inputNode(listFilter, setListFilter)}
        />
      )}
    </>
  );

  return outputNode;
};
